import type { QueryParams } from '../types/crm.common';

export const crmKeys = {
  // Leads
  leads: ['crm', 'leads'] as const,
  leadList: (params?: QueryParams) => [...crmKeys.leads, 'list', params] as const,
  leadDetail: (id: string) => [...crmKeys.leads, 'detail', id] as const,
  leadStats: () => [...crmKeys.leads, 'stats'] as const,
  leadStatsBySource: () => [...crmKeys.leads, 'stats', 'by-source'] as const,
  leadMonitorStats: () => [...crmKeys.leads, 'monitor-stats'] as const,
  leadScoreBreakdown: (id: string) => [...crmKeys.leads, 'score-breakdown', id] as const,

  // Contacts
  contacts: ['crm', 'contacts'] as const,
  contactList: (params?: QueryParams) => [...crmKeys.contacts, 'list', params] as const,
  contactDetail: (id: string) => [...crmKeys.contacts, 'detail', id] as const,
  contactStats: () => [...crmKeys.contacts, 'stats'] as const,

  // Accounts
  accounts: ['crm', 'accounts'] as const,
  accountList: (params?: QueryParams) => [...crmKeys.accounts, 'list', params] as const,
  accountDetail: (id: string) => [...crmKeys.accounts, 'detail', id] as const,

  // Deals
  deals: ['crm', 'deals'] as const,
  dealList: (params?: QueryParams) => [...crmKeys.deals, 'list', params] as const,
  dealDetail: (id: string) => [...crmKeys.deals, 'detail', id] as const,
  dealStats: () => [...crmKeys.deals, 'stats'] as const,
  dealsByPipeline: (pipelineId: string) => [...crmKeys.deals, 'pipeline', pipelineId] as const,
  dealProducts: (dealId: string) => [...crmKeys.deals, 'products', dealId] as const,

  // Tasks
  tasks: ['crm', 'tasks'] as const,
  taskList: (params?: QueryParams) => [...crmKeys.tasks, 'list', params] as const,
  taskDetail: (id: string) => [...crmKeys.tasks, 'detail', id] as const,
  taskStats: () => [...crmKeys.tasks, 'stats'] as const,
  myTasks: () => [...crmKeys.tasks, 'my'] as const,
  taskDetailsWithRelations: (id: string) => [...crmKeys.tasks, 'details', id] as const,
  taskHistory: (id: string) => [...crmKeys.tasks, 'history', id] as const,

  // Activities
  activities: ['crm', 'activities'] as const,
  activityList: (params?: QueryParams) => [...crmKeys.activities, 'list', params] as const,
  activityDetail: (id: string) => [...crmKeys.activities, 'detail', id] as const,

  // Notes
  notes: ['crm', 'notes'] as const,
  noteList: (params?: QueryParams) => [...crmKeys.notes, 'list', params] as const,
  noteDetail: (id: string) => [...crmKeys.notes, 'detail', id] as const,

  // Pipelines
  pipelines: ['crm', 'pipelines'] as const,
  pipelineList: () => [...crmKeys.pipelines, 'list'] as const,
  pipelineDetail: (id: string) => [...crmKeys.pipelines, 'detail', id] as const,

  // Archive
  archive: ['crm', 'archive'] as const,
  archivePolicies: () => [...crmKeys.archive, 'policies'] as const,
  archiveRecords: (entityType: string, params?: QueryParams) =>
    [...crmKeys.archive, 'records', entityType, params] as const,
  archiveRuns: (params?: QueryParams) => [...crmKeys.archive, 'runs', params] as const,

  // Lead Scoring
  leadScoringRules: ['crm', 'lead-scoring', 'rules'] as const,

  // Webhooks
  webhooks: ['crm', 'webhooks'] as const,
  webhookList: () => [...crmKeys.webhooks, 'list'] as const,
  webhookDetail: (id: string) => [...crmKeys.webhooks, 'detail', id] as const,
  webhookDeliveries: (id: string, params?: QueryParams) =>
    [...crmKeys.webhooks, 'deliveries', id, params] as const,

  // Account Contacts
  accountContacts: (accountId: string) => ['crm', 'accounts', accountId, 'contacts'] as const,

  // Approvals
  approvals: ['crm', 'approvals'] as const,
  pendingApprovals: () => [...crmKeys.approvals, 'pending'] as const,
  approvalsByEntity: (entityType: string, entityId: string) =>
    [...crmKeys.approvals, 'entity', entityType, entityId] as const,
  approvalWorkflows: ['crm', 'approval-workflows'] as const,
  approvalWorkflowList: (entityType?: string) =>
    [...crmKeys.approvalWorkflows, 'list', entityType] as const,
  approvalWorkflowDetail: (id: string) => [...crmKeys.approvalWorkflows, 'detail', id] as const,

  // Intake Forms (Cycle 50)
  intakeForms: ['crm', 'intake-forms'] as const,
  intakeFormList: () => [...crmKeys.intakeForms, 'list'] as const,
  intakeFormDetail: (id: string) => [...crmKeys.intakeForms, 'detail', id] as const,

  // Vendors
  vendors: ['crm', 'vendors'] as const,
  vendorList: (params?: QueryParams) => [...crmKeys.vendors, 'list', params] as const,
  vendorDetail: (id: string) => [...crmKeys.vendors, 'detail', id] as const,
  vendorCategories: () => [...crmKeys.vendors, 'categories'] as const,

  // Quotes
  quotes: ['crm', 'quotes'] as const,
  quoteList: (params?: QueryParams) => [...crmKeys.quotes, 'list', params] as const,
  quoteDetail: (id: string) => [...crmKeys.quotes, 'detail', id] as const,
  quoteDetails: (id: string) => [...crmKeys.quotes, 'details', id] as const,

  // Invoices
  invoices: ['crm', 'invoices'] as const,
  invoiceList: (params?: QueryParams) => [...crmKeys.invoices, 'list', params] as const,
  invoiceDetail: (id: string) => [...crmKeys.invoices, 'detail', id] as const,

  // Purchase Orders
  purchaseOrders: ['crm', 'purchaseOrders'] as const,
  purchaseOrderList: (params?: QueryParams) => [...crmKeys.purchaseOrders, 'list', params] as const,
  purchaseOrderDetail: (id: string) => [...crmKeys.purchaseOrders, 'detail', id] as const,
  purchaseOrderDetails: (id: string) => [...crmKeys.purchaseOrders, 'details', id] as const,

  // Cases
  cases: ['crm', 'cases'] as const,
  caseList: (params?: QueryParams) => [...crmKeys.cases, 'list', params] as const,
  caseDetail: (id: string) => [...crmKeys.cases, 'detail', id] as const,
  caseStats: () => [...crmKeys.cases, 'stats'] as const,
  caseCategories: () => [...crmKeys.cases, 'categories'] as const,
  caseTypes: () => [...crmKeys.cases, 'types'] as const,
  caseWithDetails: (id: string) => [...crmKeys.cases, 'details', id] as const,
  caseHistory: (id: string) => [...crmKeys.cases, 'history', id] as const,

  // Campaigns
  campaigns: ['crm', 'campaigns'] as const,
  campaignList: (params?: QueryParams) => [...crmKeys.campaigns, 'list', params] as const,
  campaignDetail: (id: string) => [...crmKeys.campaigns, 'detail', id] as const,
  campaignStats: () => [...crmKeys.campaigns, 'stats'] as const,
  campaignMembers: (id: string, params?: QueryParams) =>
    [...crmKeys.campaigns, 'members', id, params] as const,
  campaignMemberStats: (id: string) => [...crmKeys.campaigns, 'members', id, 'stats'] as const,
  campaignEmail: (id: string) => [...crmKeys.campaigns, 'email', id] as const,
  campaignExecutionStatus: (id: string) => [...crmKeys.campaigns, 'execution', id] as const,

  // Duplicates
  duplicates: ['crm', 'duplicates'] as const,
  leadDuplicates: (leadId: string) => [...crmKeys.duplicates, 'leads', leadId] as const,
  contactDuplicates: (contactId: string) => [...crmKeys.duplicates, 'contacts', contactId] as const,
  accountDuplicates: (accountId: string) => [...crmKeys.duplicates, 'accounts', accountId] as const,

  // SLA
  sla: ['crm', 'sla'] as const,
  slaList: (params?: QueryParams) => [...crmKeys.sla, 'list', params] as const,
  slaDetail: (id: string) => [...crmKeys.sla, 'detail', id] as const,

  // Contracts
  contracts: ['crm', 'contracts'] as const,
  contractList: (params?: QueryParams) => [...crmKeys.contracts, 'list', params] as const,
  contractDetail: (id: string) => [...crmKeys.contracts, 'detail', id] as const,
  contractAmendments: (id: string) => [...crmKeys.contracts, 'amendments', id] as const,

  // Sales Orders
  salesOrders: ['crm', 'sales-orders'] as const,
  salesOrderList: (params?: QueryParams) => [...crmKeys.salesOrders, 'list', params] as const,
  salesOrderDetail: (id: string) => [...crmKeys.salesOrders, 'detail', id] as const,
  salesOrderDetails: (id: string) => [...crmKeys.salesOrders, 'details', id] as const,

  // Forecasts
  forecasts: ['crm', 'forecasts'] as const,
  forecastList: (params?: QueryParams) => [...crmKeys.forecasts, 'list', params] as const,
  forecastDetail: (id: string) => [...crmKeys.forecasts, 'detail', id] as const,
  forecastRollup: (periodType: string, periodStart: string, periodEnd: string) =>
    [...crmKeys.forecasts, 'rollup', periodType, periodStart, periodEnd] as const,

  // Territories
  territories: ['crm', 'territories'] as const,
  territoryList: (params?: QueryParams) => [...crmKeys.territories, 'list', params] as const,
  territoryDetail: (id: string) => [...crmKeys.territories, 'detail', id] as const,
  territoryAccounts: (id: string, params?: QueryParams) =>
    [...crmKeys.territories, 'accounts', id, params] as const,

  // Competitors
  competitors: ['crm', 'competitors'] as const,
  competitorList: (params?: QueryParams) => [...crmKeys.competitors, 'list', params] as const,
  competitorDetail: (id: string) => [...crmKeys.competitors, 'detail', id] as const,
  competitorDeals: (id: string, params?: QueryParams) =>
    [...crmKeys.competitors, 'deals', id, params] as const,
  competitorAnalytics: () => [...crmKeys.competitors, 'analytics'] as const,
  dealCompetitors: (dealId: string) => ['crm', 'deals', dealId, 'competitors'] as const,

  // Attachments
  attachments: ['crm', 'attachments'] as const,
  attachmentList: (entityType: string, entityId: string) =>
    [...crmKeys.attachments, entityType, entityId] as const,

  // Import
  importJobs: ['crm', 'import', 'jobs'] as const,
  importJobDetail: (id: string) => ['crm', 'import', 'jobs', id] as const,

  // Settings
  settings: ['crm', 'settings'] as const,

  // Business Lines (inventory layer, v1.4 FR-31)
  businessLines: ['crm', 'business-lines'] as const,

  // Custom Fields
  customFields: ['crm', 'custom-fields'] as const,
  customFieldList: (entityType?: string) => ['crm', 'custom-fields', 'list', entityType] as const,
  customFieldDetail: (id: string) => ['crm', 'custom-fields', 'detail', id] as const,

  // Site Visits
  siteVisits: ['crm', 'site-visits'] as const,
  siteVisitList: (params?: QueryParams) => [...crmKeys.siteVisits, 'list', params] as const,
  siteVisitDetail: (id: string) => [...crmKeys.siteVisits, 'detail', id] as const,
  siteVisitMyDay: () => [...crmKeys.siteVisits, 'my-day'] as const,
  siteVisitStats: () => [...crmKeys.siteVisits, 'stats'] as const,
  siteVisitPhotos: (visitId: string) => [...crmKeys.siteVisits, 'photos', visitId] as const,

  // Products
  products: ['crm', 'products'] as const,
  productList: (params?: QueryParams) => [...crmKeys.products, 'list', params] as const,
  productDetail: (id: string) => [...crmKeys.products, 'detail', id] as const,
  productCategories: () => [...crmKeys.products, 'categories'] as const,

  // Dashboard
  dashboard: ['crm', 'dashboard'] as const,
  dashboardData: (period?: string) => [...crmKeys.dashboard, 'data', period] as const,
  salesFunnel: (period?: string) => [...crmKeys.dashboard, 'sales-funnel', period] as const,
  revenueTrend: () => [...crmKeys.dashboard, 'revenue-trend'] as const,

  // Search (Sprint 10)
  search: (query: string, types?: string[]) => ['crm', 'search', query, types] as const,

  // Notifications
  notifications: ['notifications'] as const,
  notificationList: (params?: QueryParams) => [...crmKeys.notifications, 'list', params] as const,
  unreadCount: () => [...crmKeys.notifications, 'unread-count'] as const,
  notificationPreferences: () => [...crmKeys.notifications, 'preferences'] as const,

  // Payments
  payments: ['crm', 'payments'] as const,
  paymentList: (params?: QueryParams) => [...crmKeys.payments, 'list', params] as const,
  paymentDetail: (id: string) => [...crmKeys.payments, 'detail', id] as const,

  // Credit Notes
  creditNotes: ['crm', 'credit-notes'] as const,
  creditNoteList: (params?: QueryParams) => [...crmKeys.creditNotes, 'list', params] as const,
  creditNoteDetail: (id: string) => [...crmKeys.creditNotes, 'detail', id] as const,

  // Payment Terms
  paymentTerms: ['crm', 'payment-terms'] as const,
  paymentTermList: () => [...crmKeys.paymentTerms, 'list'] as const,

  // Payment Methods
  paymentMethods: ['crm', 'payment-methods'] as const,
  paymentMethodList: () => [...crmKeys.paymentMethods, 'list'] as const,

  // Tax
  taxRegimes: ['crm', 'tax', 'regimes'] as const,
  taxRegimeList: () => [...crmKeys.taxRegimes, 'list'] as const,
  taxRegimeDetail: (id: string) => [...crmKeys.taxRegimes, 'detail', id] as const,
  taxComponents: (regimeId: string) => ['crm', 'tax', 'components', regimeId] as const,
  taxGroups: ['crm', 'tax', 'groups'] as const,
  taxGroupList: () => [...crmKeys.taxGroups, 'list'] as const,
  taxGroupDetail: (id: string) => [...crmKeys.taxGroups, 'detail', id] as const,

  // Price Books
  priceBooks: ['crm', 'price-books'] as const,
  priceBookList: () => [...crmKeys.priceBooks, 'list'] as const,
  priceBookDetail: (id: string) => [...crmKeys.priceBooks, 'detail', id] as const,

  // Discount Rules
  discountRules: ['crm', 'discount-rules'] as const,
  discountRuleList: () => [...crmKeys.discountRules, 'list'] as const,

  // Exchange Rates
  exchangeRates: ['crm', 'exchange-rates'] as const,
  exchangeRateList: (fromCurrency?: string, toCurrency?: string) =>
    [...crmKeys.exchangeRates, 'list', fromCurrency, toCurrency] as const,

  // Document Flow
  documentFlow: (entityType: string, entityId: string) =>
    ['crm', 'document-flow', entityType, entityId] as const,

  // Entity History (generic)
  entityHistory: (entityType: string, entityId: string) =>
    ['crm', 'history', entityType, entityId] as const,
};
